import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, AuthRoute, ProtectedRoute, useAuth } from '../global/contexts/auth/auth';
import { queryClient } from '../core/network/query-client';
import AdminShell from './admin-shell';
import FarmsPage from '../pages/admin/farms';

import LoginPage from '../pages/login';
import UnauthorizedPage from '../pages/unauthorized';
import DashboardPage from '../pages/admin/dashboard';
import ClientsPage from '../pages/admin/clients';
import AnimalsPage from '../pages/admin/animals';
import TorosPage from '../pages/admin/toros';
import RecipientsPage from '../pages/admin/recipients';
import RacesPage from '../pages/admin/races';
import LabsPage from '../pages/admin/labs';
import SemenPage from '../pages/admin/semen';
import EmbryosPage from '../pages/admin/embryos';
import OpuPage from '../pages/admin/opu';
import DxPage from '../pages/admin/dx';
import TasksPage from '../pages/admin/tasks';
import PlanningPage from '../pages/admin/planning';
import OpuPlanningPage from '../pages/admin/opu-planning';
import PartosPage from '../pages/admin/partos';
import AsocebuPage from '../pages/admin/asocebu';
import SalesPage from '../pages/admin/sales';
import ProjectedCrossesPage from '../pages/admin/projected-crosses';
import BillingPage from '../pages/admin/billing';
import SgPage from '../pages/admin/sg';
import NotificationsPage from '../pages/admin/notifications';
import UsersPage from '../pages/admin/users';
import OpuSessionPage from '../pages/opu-session';
import OpuEditPage from '../pages/opu-edit';
import ClivageSessionPage from '../pages/clivage-session';
import ClivageEditPage from '../pages/clivage-edit';
import TransferSessionPage from '../pages/transfer-session';
import TransferEditPage from '../pages/transfer-edit';
import TransferDetailPage from '../pages/transfer-detail';
import ExamplePage from '../pages/admin/example';
import ExampleCatalogPage from '../pages/admin/example-catalog';
import AuditPage from '../pages/admin/audit';
import { ConfirmProvider } from '../common/components/ConfirmDialog/confirm';

// TSS vite/01 — app shell. Wires providers, router, layout chrome.
// TSS vite/03 §"Provider composition order" — Router → Auth → QueryClient → cross-app providers.

export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <QueryClientProvider client={queryClient}>
                    <ConfirmProvider>
                        <AppGate>
                            <AppRouter />
                        </AppGate>
                    </ConfirmProvider>
                    <Toaster position="top-right" toastOptions={{ error: { ariaProps: { role: 'alert', 'aria-live': 'assertive' } } }} />
                </QueryClientProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

// Blocks rendering until the auth bootstrap resolves so guarded routes
// never flash unauthorized content during the initial /auth/me call.
function AppGate({ children }) {
    const { initializing } = useAuth();
    if (initializing) {
        return (
            <div className="boot-loader">
                <div className="boot-loader__spinner" />
                <span className="boot-loader__text">Verificando sesión...</span>
            </div>
        );
    }
    return children;
}

function AppRouter() {
    return (
        <Routes>
            <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />

            {/* Pre-auth surfaces — TSS vite/01 §"Pages by audience". */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/logout" element={<Navigate to="/login?logout=true" replace />} />

            {/* Full-screen OPU items workspace — focused, no sidebar (opens in its own tab). */}
            <Route path="/opu/session" element={
                <ProtectedRoute domain="aspirations" action="write"><OpuSessionPage /></ProtectedRoute>
            } />
            <Route path="/opu/edit" element={
                <ProtectedRoute domain="aspirations" action="write"><OpuEditPage /></ProtectedRoute>
            } />
            <Route path="/opu/clivage" element={
                <ProtectedRoute domain="clivages" action="write"><ClivageSessionPage /></ProtectedRoute>
            } />
            <Route path="/opu/clivage/edit" element={
                <ProtectedRoute domain="clivages" action="write"><ClivageEditPage /></ProtectedRoute>
            } />
            <Route path="/opu/transfer" element={
                <ProtectedRoute domain="transfers" action="write"><TransferSessionPage /></ProtectedRoute>
            } />
            <Route path="/opu/transfer/edit" element={
                <ProtectedRoute domain="transfers" action="write"><TransferEditPage /></ProtectedRoute>
            } />

            {/* Authenticated surfaces — sidebar layout + permission-gated pages. */}
            <Route path="/admin" element={<AuthRoute><AdminShell /></AuthRoute>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="tasks" element={
                    <ProtectedRoute domain="kpis" action="read"><TasksPage /></ProtectedRoute>
                } />
                <Route path="planning" element={
                    <ProtectedRoute domain="kpis" action="read"><PlanningPage /></ProtectedRoute>
                } />
                <Route path="planning/dx" element={
                    <ProtectedRoute domain="kpis" action="read"><PlanningPage /></ProtectedRoute>
                } />
                <Route path="opu-planning" element={
                    <ProtectedRoute domain="kpis" action="read"><OpuPlanningPage /></ProtectedRoute>
                } />

                {/* Reproducción */}
                <Route path="opu" element={
                    <ProtectedRoute domain="aspirations" action="read"><OpuPage /></ProtectedRoute>
                } />
                <Route path="opu/new" element={
                    <ProtectedRoute domain="aspirations" action="write"><OpuPage /></ProtectedRoute>
                } />
                <Route path="opu/:id" element={
                    <ProtectedRoute domain="aspirations" action="read"><OpuPage /></ProtectedRoute>
                } />
                <Route path="dx" element={
                    <ProtectedRoute domain="pregnancies" action="read"><DxPage /></ProtectedRoute>
                } />
                <Route path="partos" element={
                    <ProtectedRoute domain="births" action="read"><PartosPage /></ProtectedRoute>
                } />
                <Route path="asocebu" element={
                    <ProtectedRoute domain="asocebu" action="read"><AsocebuPage /></ProtectedRoute>
                } />
                <Route path="transfer/:id" element={
                    <ProtectedRoute domain="transfers" action="read"><TransferDetailPage /></ProtectedRoute>
                } />

                {/* Catálogo — CRUD por recurso (features en construcción). */}
                <Route path="animals" element={
                    <ProtectedRoute domain="animals" action="read"><AnimalsPage /></ProtectedRoute>
                } />
                <Route path="animals/new" element={
                    <ProtectedRoute domain="animals" action="write"><AnimalsPage /></ProtectedRoute>
                } />
                <Route path="animals/:id" element={
                    <ProtectedRoute domain="animals" action="read"><AnimalsPage /></ProtectedRoute>
                } />
                <Route path="animals/:id/edit" element={
                    <ProtectedRoute domain="animals" action="write"><AnimalsPage /></ProtectedRoute>
                } />
                <Route path="animals/:id/stats" element={
                    <ProtectedRoute domain="kpis" action="read"><AnimalsPage /></ProtectedRoute>
                } />
                <Route path="animals/:id/liquidation" element={
                    <ProtectedRoute domain="billing" action="read"><AnimalsPage /></ProtectedRoute>
                } />
                <Route path="toros" element={
                    <ProtectedRoute domain="animals" action="read"><TorosPage /></ProtectedRoute>
                } />
                <Route path="recipients" element={
                    <ProtectedRoute domain="recipients" action="read"><RecipientsPage /></ProtectedRoute>
                } />
                <Route path="recipients/:id/stats" element={
                    <ProtectedRoute domain="recipients" action="read"><RecipientsPage /></ProtectedRoute>
                } />
                <Route path="semen" element={
                    <ProtectedRoute domain="semen" action="read"><SemenPage /></ProtectedRoute>
                } />
                <Route path="semen/new" element={
                    <ProtectedRoute domain="semen" action="write"><SemenPage /></ProtectedRoute>
                } />
                <Route path="semen/:id" element={
                    <ProtectedRoute domain="semen" action="read"><SemenPage /></ProtectedRoute>
                } />
                <Route path="semen/:id/edit" element={
                    <ProtectedRoute domain="semen" action="write"><SemenPage /></ProtectedRoute>
                } />
                <Route path="semen/:id/stats" element={
                    <ProtectedRoute domain="kpis" action="read"><SemenPage /></ProtectedRoute>
                } />

                <Route path="races" element={
                    <ProtectedRoute domain="races" action="read"><RacesPage /></ProtectedRoute>
                } />
                <Route path="races/new" element={
                    <ProtectedRoute domain="races" action="write"><RacesPage /></ProtectedRoute>
                } />
                <Route path="races/:id" element={
                    <ProtectedRoute domain="races" action="read"><RacesPage /></ProtectedRoute>
                } />
                <Route path="races/:id/edit" element={
                    <ProtectedRoute domain="races" action="write"><RacesPage /></ProtectedRoute>
                } />

                <Route path="labs" element={
                    <ProtectedRoute domain="labs" action="read"><LabsPage /></ProtectedRoute>
                } />
                <Route path="labs/new" element={
                    <ProtectedRoute domain="labs" action="write"><LabsPage /></ProtectedRoute>
                } />
                <Route path="labs/:id" element={
                    <ProtectedRoute domain="labs" action="read"><LabsPage /></ProtectedRoute>
                } />
                <Route path="labs/:id/edit" element={
                    <ProtectedRoute domain="labs" action="write"><LabsPage /></ProtectedRoute>
                } />
                <Route path="labs/:id/stats" element={
                    <ProtectedRoute domain="kpis" action="read"><LabsPage /></ProtectedRoute>
                } />
                <Route path="embryos" element={
                    <ProtectedRoute domain="embryos" action="read"><EmbryosPage /></ProtectedRoute>
                } />
                <Route path="embryos/new" element={
                    <ProtectedRoute domain="embryos" action="write"><EmbryosPage /></ProtectedRoute>
                } />
                <Route path="embryos/:id" element={
                    <ProtectedRoute domain="embryos" action="read"><EmbryosPage /></ProtectedRoute>
                } />
                <Route path="farms" element={
                    <ProtectedRoute domain="farms" action="read"><FarmsPage /></ProtectedRoute>
                } />
                <Route path="farms/:id" element={
                    <ProtectedRoute domain="kpis" action="read"><FarmsPage /></ProtectedRoute>
                } />
                <Route path="clients" element={
                    <ProtectedRoute domain="clients" action="read"><ClientsPage /></ProtectedRoute>
                } />
                <Route path="clients/new" element={
                    <ProtectedRoute domain="clients" action="write"><ClientsPage /></ProtectedRoute>
                } />
                <Route path="clients/:id" element={
                    <ProtectedRoute domain="clients" action="read"><ClientsPage /></ProtectedRoute>
                } />
                <Route path="clients/:id/edit" element={
                    <ProtectedRoute domain="clients" action="write"><ClientsPage /></ProtectedRoute>
                } />
                <Route path="clients/:id/stats" element={
                    <ProtectedRoute domain="kpis" action="read"><ClientsPage /></ProtectedRoute>
                } />
                <Route path="clients/:id/liquidation" element={
                    <ProtectedRoute domain="billing" action="read"><ClientsPage /></ProtectedRoute>
                } />
                <Route path="clients/:id/account" element={
                    <ProtectedRoute domain="billing" action="read"><ClientsPage /></ProtectedRoute>
                } />

                {/* Negocios — ventas de crías (admin-sales.md; gatea sobre clients). */}
                <Route path="sales" element={
                    <ProtectedRoute domain="clients" action="read"><SalesPage /></ProtectedRoute>
                } />
                {/* Cruces proyecto — catálogo (admin-projected-crosses.md). */}
                <Route path="projected-crosses" element={
                    <ProtectedRoute domain="projected_crosses" action="read"><ProjectedCrossesPage /></ProtectedRoute>
                } />
                {/* Facturación — Capa 5 (admin-billing-*.md; gatea sobre billing). */}
                <Route path="billing" element={
                    <ProtectedRoute domain="billing" action="read"><BillingPage /></ProtectedRoute>
                } />
                <Route path="billing/liquidations/:id" element={
                    <ProtectedRoute domain="billing" action="read"><BillingPage /></ProtectedRoute>
                } />

                {/* Integraciones — Software Ganadero (admin-sg.md; gatea sobre sg). */}
                <Route path="sg" element={
                    <ProtectedRoute domain="sg" action="read"><SgPage /></ProtectedRoute>
                } />

                {/* Servicios — mensajería (admin-notifications.md; gatea sobre notifications). */}
                <Route path="notifications" element={
                    <ProtectedRoute domain="notifications" action="read"><NotificationsPage /></ProtectedRoute>
                } />
                <Route path="notifications/new" element={
                    <ProtectedRoute domain="notifications" action="write"><NotificationsPage /></ProtectedRoute>
                } />
                <Route path="notifications/:code/edit" element={
                    <ProtectedRoute domain="notifications" action="write"><NotificationsPage /></ProtectedRoute>
                } />

                {/* Administración — usuarios y permisos (admin-users.md; gatea sobre users). */}
                <Route path="users" element={
                    <ProtectedRoute domain="users" action="read"><UsersPage /></ProtectedRoute>
                } />
                <Route path="users/:id" element={
                    <ProtectedRoute domain="users" action="read"><UsersPage /></ProtectedRoute>
                } />

                {/* Auditoría — acciones administrativas sensibles. Merges (unificar duplicados,
                    admin-merge.md). Gatea sobre `<entidad>:admin` (admin/webmaster por wildcard). */}
                <Route path="audit" element={<Navigate to="/admin/audit/merges" replace />} />
                <Route path="audit/merges" element={
                    <ProtectedRoute domain="animals" action="admin"><AuditPage /></ProtectedRoute>
                } />
                <Route path="audit/eliminaciones" element={
                    <ProtectedRoute domain="aspirations" action="admin"><AuditPage /></ProtectedRoute>
                } />

                {/* Reference feature (template) — kept for TSS pattern reference. */}
                <Route path="example" element={
                    <ProtectedRoute any={['items:read', 'items:write']}><ExamplePage /></ProtectedRoute>
                } />
                <Route path="example/:id" element={
                    <ProtectedRoute any={['items:read', 'items:write']}><ExamplePage /></ProtectedRoute>
                } />
                <Route path="example/categories" element={
                    <ProtectedRoute domain="items:read"><ExampleCatalogPage /></ProtectedRoute>
                } />
            </Route>

            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
        </Routes>
    );
}
